import { FormResponseStatus } from "@/models/form-types";
import { CRMVertical, CRMVerticalKey } from "@/models/crm-types";

// ─── Default Pipeline Stages ─────────────────────────────────────────

export const DEFAULT_PIPELINE_STAGES: FormResponseStatus[] = [
  FormResponseStatus.IN_REVIEW,
  FormResponseStatus.ACCEPTED,
  FormResponseStatus.REJECTED,
];

export const FULL_PIPELINE_STAGES: FormResponseStatus[] = [
  FormResponseStatus.IN_REVIEW,
  FormResponseStatus.ACCEPTED,
  FormResponseStatus.CONFIRMED,
  FormResponseStatus.ONBOARDED,
];

// Kanban columns show these stages (REJECTED hidden by default)
export const KANBAN_VISIBLE_STAGES: FormResponseStatus[] = [
  FormResponseStatus.IN_REVIEW,
  FormResponseStatus.ACCEPTED,
  FormResponseStatus.CONFIRMED,
  FormResponseStatus.ONBOARDED,
];

// ─── Vertical Definitions ────────────────────────────────────────────

export const CRM_VERTICALS: CRMVertical[] = [
  {
    key: "fashion",
    label: "Fashion",
    color: "#ff983a",
    bgColor: "rgba(255, 152, 58, 0.2)",
    gradient: "linear-gradient(to top, #ebe7e3, #fff0e1, #fff7e5)",
    cssClass: "vertical-fashion-bg",
    icon: "Shirt",
    personas: [
      { key: "designer", label: "Designer", formId: "fashion/designer", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "buyer", label: "Buyer", formId: "fashion/buyer", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "stylist", label: "Stylist", formId: "fashion/stylist", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "mua", label: "MUA", formId: "fashion/mua", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "model", label: "Model", formId: "fashion/model", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "professionals", label: "Professionals", formId: "fashion/professionals", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
      { key: "photographer-february", label: "Photographer (Feb 25)", formId: "fashion/photographer-february-2027", pipelineStages: [...FULL_PIPELINE_STAGES] },
    ],
  },
  {
    key: "tech",
    label: "Tech",
    color: "#00594f",
    bgColor: "rgba(0, 89, 79, 0.2)",
    gradient: "linear-gradient(to top, #dbe5e4, #e6f0e8, #f4f9ef)",
    cssClass: "vertical-tech-bg",
    icon: "Cpu",
    personas: [
      { key: "elevate", label: "Elevate", formId: "tech/elevate", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
      { key: "celevateher", label: "CElevateHer", formId: "tech/celevateher", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
      { key: "cblack", label: "CBlack", formId: "tech/cblack", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
      { key: "speaker", label: "Speaker", formId: "tech/speaker", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "investor", label: "Investor", formId: "tech/investor", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "engineers", label: "Engineers", formId: "tech/engineers", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
      { key: "responsible-deep-tech", label: "Responsible Deep Tech", formId: "tech/responsible-deep-tech", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
      { key: "exec-luncheon", label: "Exec Luncheon", formId: "tech/exec-luncheon/general-admission2k25", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "cblack-semi-finals", label: "CBlack Semi-Finals", formId: "tech/cblack-semi-finals", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
      { key: "celevateher-semi-finals", label: "CElevateHer Semi-Finals", formId: "tech/celevateher-semi-finals", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
      { key: "startup-alpha", label: "Startup Alpha", formId: "tech/startup-alpha", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
    ],
  },
  {
    key: "gaming",
    label: "Gaming",
    color: "#2f3068",
    bgColor: "rgba(47, 48, 104, 0.2)",
    gradient: "linear-gradient(to top, #ebf3f9, #e2e7f1, #e0e1e7)",
    cssClass: "vertical-gaming-bg",
    icon: "Gamepad2",
    personas: [
      { key: "esports-overwatch", label: "Esports (Overwatch)", formId: "gaming/esports/overwatch", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
      { key: "speaker", label: "Speaker", formId: "gaming/speaker", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "developer", label: "Developer", formId: "gaming/developer", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "pitch-quest", label: "Pitch Quest", formId: "gaming/pitch-quest", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
      { key: "playtest-zone", label: "Playtest Zone", formId: "gaming/playtest-zone", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
      { key: "ctjam", label: "CTJAM", formId: "gaming/ctjam", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
    ],
  },
  {
    key: "music",
    label: "Music",
    color: "#7a428d",
    bgColor: "rgba(122, 66, 141, 0.2)",
    gradient: "linear-gradient(to top, #e3eaf1, #ededf9, #fbf2f9)",
    cssClass: "vertical-music-bg",
    icon: "Music",
    personas: [
      { key: "artist", label: "Artist", formId: "music/artist", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "publisher", label: "Publisher", formId: "music/publisher", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "publisher-publisher", label: "Publisher (Publisher)", formId: "music/publisher-publisher", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
    ],
  },
  {
    key: "culture",
    label: "Culture",
    color: "#ef435e",
    bgColor: "rgba(239, 67, 94, 0.2)",
    gradient: "linear-gradient(to top, #fffbee, #fef0e9, #f3e3e6)",
    cssClass: "vertical-culture-bg",
    icon: "Palette",
    personas: [
      { key: "innovation-zone", label: "Innovation Zone", formId: "innovation-zone", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
      { key: "diversity-pass", label: "Diversity Pass", formId: "diversity-pass", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
    ],
  },
  {
    key: "film",
    label: "Film",
    color: "#00a399",
    bgColor: "rgba(0, 163, 153, 0.2)",
    gradient: "linear-gradient(to top, #e2e2e5, #e6e2e9, #e9e3ec)",
    cssClass: "vertical-film-bg",
    icon: "Film",
    personas: [
      { key: "speaker", label: "Speaker", formId: "general/speaker", pipelineStages: [...FULL_PIPELINE_STAGES] },
    ],
  },
  {
    key: "operations",
    label: "Operations",
    color: "#8985d5",
    bgColor: "rgba(137, 133, 213, 0.2)",
    gradient: "linear-gradient(to top, #fbf2f9, #ededf9, #e3eaf1)",
    cssClass: "vertical-operations-bg",
    icon: "Settings",
    personas: [
      { key: "vendor", label: "Vendor", formId: "vendor", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "volunteer", label: "Volunteer", formId: "volunteer", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "photographer", label: "Photographer/Videographer", formId: "photographer", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "vip-dinner", label: "VIP Dinner", formId: "vip-dinner", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
      { key: "resume-upload", label: "Resume Upload", formId: "resumeupload", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
    ],
  },
  {
    key: "media",
    label: "Media",
    color: "#777E90",
    bgColor: "rgba(119, 126, 144, 0.2)",
    gradient: "linear-gradient(to top, #e8e9ec, #edeef1, #f2f3f6)",
    cssClass: "vertical-media-bg",
    icon: "Megaphone",
    personas: [
      { key: "media-press", label: "Media/Press", formId: "media-press", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
      { key: "influencer", label: "Influencer", formId: "influencer", pipelineStages: [...FULL_PIPELINE_STAGES] },
      { key: "b2b", label: "B2B", formId: "b2b", pipelineStages: [...DEFAULT_PIPELINE_STAGES] },
    ],
  },
];

// ─── Lookup Helpers ──────────────────────────────────────────────────

/** Get a vertical by key */
export const getVertical = (key: CRMVerticalKey): CRMVertical | undefined =>
  CRM_VERTICALS.find((v) => v.key === key);

/** Get all formIds for a vertical */
export const getFormIdsForVertical = (verticalKey: CRMVerticalKey): string[] => {
  const vertical = getVertical(verticalKey);
  return vertical ? vertical.personas.map((p) => p.formId) : [];
};

/** Map a formId to its vertical key */
export const getVerticalForFormId = (formId: string): CRMVerticalKey | undefined => {
  for (const vertical of CRM_VERTICALS) {
    if (vertical.personas.some((p) => p.formId === formId)) {
      return vertical.key;
    }
  }
  return undefined;
};

/** Map a formId to its persona */
export const getPersonaForFormId = (formId: string) => {
  for (const vertical of CRM_VERTICALS) {
    const persona = vertical.personas.find((p) => p.formId === formId);
    if (persona) {
      return { ...persona, verticalKey: vertical.key };
    }
  }
  return undefined;
};

/** Get all formIds across all verticals (for global queries) */
export const ALL_CRM_FORM_IDS: string[] = CRM_VERTICALS.flatMap((v) =>
  v.personas.map((p) => p.formId)
);

// ─── Status Display Config ───────────────────────────────────────────

export const STATUS_CONFIG: Record<FormResponseStatus, { label: string; order: number }> = {
  [FormResponseStatus.NOT_SUBMITTED]: { label: "Not Submitted", order: 0 },
  [FormResponseStatus.IN_REVIEW]: { label: "In Review", order: 1 },
  [FormResponseStatus.ACCEPTED]: { label: "Accepted", order: 2 },
  [FormResponseStatus.REJECTED]: { label: "Rejected", order: 3 },
  [FormResponseStatus.CONFIRMED]: { label: "Confirmed", order: 4 },
  [FormResponseStatus.ONBOARDED]: { label: "Onboarded", order: 5 },
};

// ─── Firestore Collection Names ──────────────────────────────────────

export const CRM_COLLECTIONS = {
  PARTICIPANT_NOTES: "crm_participant_notes",
  ACTIVITY_LOG: "crm_activity_log",
} as const;
